import { Combobox, Transition } from "@headlessui/react";
import { ChevronDownIcon, PlusIcon } from "@heroicons/react/24/outline";
import { Fragment, useEffect, useRef, useState } from "react";
import { Control, Controller, FieldPath } from "react-hook-form";
//@ts-ignore
import { tw } from "@careinfo/tailwind";
import { useDebounce } from "../../../customHooks/useDebounceHook";
import useGetHook from "../../../customHooks/useGetHook";

interface Props<T> {
  label: string;
  queryKey: string;
  params: {
    [key: string]: any;
  };
  name: FieldPath<T>;
  control: Control<T, any>;
  url: string;
  placeholder?: string;
  limit?: number;
  disabled?: boolean;
}

const ComboboxFilter = <T,>({
  url,
  queryKey,
  params,
  name,
  control,
  limit = 10,
  label,
  placeholder,
  disabled = false,
}: Props<T>) => {
  const [query, setQuery] = useState("");
  const comboboxButtonRef = useRef<HTMLButtonElement>(null);
  const [data, setData] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const comboboxRef = useRef<HTMLDivElement>(null);
  const debounceSearch = useDebounce(query);

  const filteredData: any =
    query === ""
      ? data
      : data.filter((item: any) =>
          item?.name
            .toLowerCase()
            .replace(/\s+/g, "")
            .includes(query.toLowerCase().replace(/\s+/g, ""))
        );

  const { data: serverData } = useGetHook({
    queryKey: [queryKey, `${queryKey}-${limit}-${debounceSearch}`],
    url: url,
    params: {
      limit: limit,
      offset: 0,
      search: debounceSearch,
      ...params,
    },
  });

  useEffect(() => {
    if (Array.isArray(serverData?.results)) {
      setData(serverData?.results ?? []);
    }
  }, [serverData]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        comboboxRef.current &&
        !comboboxRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false); // Close the combobox
      }
    };
    const handleFocusOutside = () => {
      if (
        comboboxRef.current &&
        !comboboxRef.current.contains(document.activeElement as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("focusin", handleFocusOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("focusin", handleFocusOutside);
    };
  }, []);

  return (
    <Controller
      control={control}
      name={name}
      defaultValue={null}
      render={({ field, fieldState: { error } }) => (
        <Combobox
          disabled={disabled}
          {...field}
          onChange={(value) => {
            field.onChange(value);
            setIsOpen(false); // Close combobox on selection
          }}
        >
          <div ref={comboboxRef} className={tw`relative mt-3`}>
            <label htmlFor={name}>{label}</label>
            <div
              className={tw`relative w-full cursor-default overflow-hidden bg-white text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-opacity-75 focus-visible:ring-offset-2 focus-visible:ring-offset-teal-300 sm:text-sm mt-2`}
            >
              <Combobox.Input
                className={tw`w-full focus:outline-none bg-[#EEF5F4] p-[10px] text-left`}
                placeholder={placeholder}
                autoComplete="off"
                displayValue={(item: any) => item?.name}
                onChange={(event) => {
                  setQuery(event.target.value);
                  setIsOpen(true);
                }}
                onClick={() => setIsOpen(true)}
                id={name}
              />
              <Combobox.Button
                ref={comboboxButtonRef}
                className={tw`absolute inset-y-0 right-0 flex items-center pr-2`}
                onClick={() => setIsOpen(!isOpen)}
              >
                {field.value && (
                  <PlusIcon
                    className={tw`h-5 w-5 text-gray-400 rotate-45`}
                    aria-hidden="true"
                    onClick={(e) => {
                      e.stopPropagation();
                      field.onChange(null);
                    }}
                  />
                )}
                <ChevronDownIcon
                  className={tw`h-5 w-5 text-gray-400`}
                  aria-hidden="true"
                  onClick={(e) => {
                    e.stopPropagation();
                    field.onChange(null);
                  }}
                />
              </Combobox.Button>
            </div>
            <Transition
              as={Fragment}
              show={isOpen}
              leave="transition ease-in duration-100"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
              afterLeave={() => setQuery("")}
            >
              <Combobox.Options
                className={tw`absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm`}
              >
                {filteredData?.length === 0 && query !== "" ? (
                  <div
                    className={tw`relative cursor-default select-none py-2 px-4 text-gray-700`}
                  >
                    Nothing found.
                  </div>
                ) : (
                  filteredData.map((person: any) => (
                    <Combobox.Option
                      key={person.id}
                      className={({ active }) =>
                        tw`relative hover:bg-primary hover:text-white hover:cursor-pointer cursor-default select-none py-2 pl-10 pr-4 ${
                          active ? tw`bg-primary text-white` : tw`text-gray-900`
                        }`
                      }
                      value={person}
                    >
                      {({ selected, active }) => (
                        <>
                          <span
                            className={tw`block truncate ${
                              selected ? tw`font-medium` : tw`font-normal`
                            }`}
                          >
                            {person.name}
                          </span>
                          {selected ? (
                            <span
                              className={tw`absolute inset-y-0 left-0 flex items-center pl-3 ${
                                active ? tw`text-white` : tw`text-teal-600`
                              }`}
                            >
                              {/* <CheckIcon className="h-5 w-5" aria-hidden="true" /> */}
                            </span>
                          ) : null}
                        </>
                      )}
                    </Combobox.Option>
                  ))
                )}
              </Combobox.Options>
            </Transition>
            {error && (
              <p className={tw`text-red-500 text-xs my-1`}>{error?.message}</p>
            )}
          </div>
        </Combobox>
      )}
    />
  );
};

export default ComboboxFilter;
